'use client';

import React from 'react';

import { usePathname } from 'next/navigation';

import { Footer } from '@components/HeaderFooter';
import { OmniBox } from '@components/OmniBox';
import { SignedOutAuthCard } from '@components/SignedOutAuthCard';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import { useTheme } from '@mui/material/styles';

import { useCompose } from '@/context/ComposeContext';
import { useUserInfo } from '@/context/UserInfoContext';
import { useLayoutCalculations } from '@/hooks/useLayoutCalculations';
import { useResponsiveLayout } from '@/hooks/useResponsiveLayout';

interface LayoutWrapperProps {
  children: React.ReactNode;
}

export const LayoutWrapper: React.FC<LayoutWrapperProps> = ({ children }) => {
  const pathname = usePathname();
  const { userInfoState } = useUserInfo();
  const { isComposing } = useCompose();
  const theme = useTheme(); // Note: Don't use theme.palette values directly in styles - use CSS variables instead!

  // Check if we're on a doc page (which might have context panel)
  const isDocPage = pathname?.startsWith('/doc/');

  // Hide floating widgets when composing on doc pages
  const shouldHideFloatingWidgets = isComposing && isDocPage;
  const shouldShowSignedOutUi = userInfoState.status === 'signed_out';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box
        component="main"
        role="main"
        aria-label="Main content"
        sx={{
          flex: '1 0 auto',
          margin: '1em',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1, // Consistent top padding
          pb: shouldShowSignedOutUi ? 2 : 0, // Add bottom padding only for signed-out UI
          '@media (max-width: 768px)': {
            marginLeft: '0.2em',
            marginRight: '0.2em',
          },
          [theme.breakpoints.down('sm')]: isDocPage
            ? {
                marginLeft: 0,
                marginRight: 0,
              }
            : undefined,
        }}
      >
        {children}
      </Box>

      {/* hiding footer for now

      <Footer /> */}

      {/* Gradient overlay - fades content to background */}
      {/*
        IMPORTANT: Use CSS variables for theme colors, not theme.palette values!
        See frontend/src/theme/COLOR_SYSTEM_SPEC.md for details.
        Example: var(--mui-palette-background-default) instead of theme.palette.background.default
      */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '150px',
          background:
            'linear-gradient(to bottom, transparent 0%, var(--mui-palette-background-default) 100%)',
          pointerEvents: 'none',
          zIndex: 10, // Above content but below OmniBox
        }}
      />

      {/* Spacer for floating widgets at bottom */}
      <Box
        sx={{
          height: shouldShowSignedOutUi
            ? `calc(80px + env(safe-area-inset-bottom))` // SignedOutAuthCard (64px + 16px)
            : isDocPage
              ? `calc(128px + env(safe-area-inset-bottom))` // Context pills (48px + 8px) + OmniBox (48px + 16px) + spacing
              : `calc(64px + env(safe-area-inset-bottom))`, // Just OmniBox (48px + 16px)
          flexShrink: 0,
        }}
      />

      {/* OmniBox is positioned fixed at bottom */}
      {!shouldHideFloatingWidgets && <OmniBox />}

      {/* SignedOutAuthCard for logged-out users */}
      {!shouldHideFloatingWidgets && <SignedOutAuthCard />}
    </Box>
  );
};
